import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'; // Assurez-vous que ceci est bien importé
import { AuthService, Product, ProductAttribute, Color, Size, Length, CartOrderItem, Order } from '../auth.service';
import { CartService } from '../cart.service';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
interface GroupedAttributes {
  product: Product;
  attributes: (ProductAttribute & {
    imageUrl?: string | null;
    textureUrl?: string | null;
    videoUrl?: string | null;
  })[];
  selectedColorId: number | null;
  selectedSizeId: number | null;
  selectedLengthId: number | null;
  availableColors: Color[];
  availableSizes: Size[];
  availableLengths: Length[];
  filteredAttributes: (ProductAttribute & {
    imageUrl?: string | null;
    textureUrl?: string | null;
    videoUrl?: string | null;
  })[];
}

@Component({
  selector: 'app-productlist',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './productlist.component.html',
  styleUrl: './productlist.component.css'
})
export class ProductlistComponent implements OnInit {
  productGroups: GroupedAttributes[] = [];
  userId!: number;

  constructor(
    private authService: AuthService,
    private cartService: CartService,
    private router: Router,
    private meta: Meta,
    private titleService: Title) { }

  ngOnInit(): void {
    this.userId = this.getCurrentUserId();
    this.loadProductAttributes();
    this.titleService.setTitle("Cognitiex: Explore Our Product Attributes");
    this.meta.addTags([
      { name: 'description', content: 'Discover a variety of services, projects, and innovative solutions offered by Cognitiex. Filter attributes to find what suits your needs best.' },
      { name: 'keywords', content: 'services, projects, innovation, AI, customization, attributes' },
      { name: 'author', content: 'Cognitiex' },
      { property: 'og:title', content: "Cognitiex: Explore Our Product Attributes" },
      { property: 'og:description', content: 'Filter and explore our diverse range of services and projects. Your path to innovation starts here.' },
      { property: 'og:image', content: '/logo.png' },
      { property: 'og:url', content: 'https://www.cognitiex.com/attributes' },
      { property: 'og:type', content: 'website' },
      { name: 'robots', content: 'index, follow' },
      { name: 'language', content: 'en' },
      { name: 'distribution', content: 'global' },
      { name: 'rating', content: 'general' },
      { name: 'theme-color', content: '#3a6cf4' },
      { property: 'og:site_name', content: 'Cognitiex' },
      { property: 'og:locale', content: 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@Cognitiex' },
      { name: 'twitter:title', content: 'Cognitiex: Explore Our Product Attributes' },
      { name: 'twitter:description', content: 'Find the perfect match for your needs from our services and projects. Simplify your decision-making with detailed filters.' },
      { name: 'twitter:image', content: '/logo.png' },
      { name: 'twitter:image:alt', content: 'Cognitiex Logo' }
    ]);
  }

  private getCurrentUserId(): number {
    return 1; // Remplacez ceci par la logique réelle pour récupérer l'ID utilisateur
  }

  loadProductAttributes(): void {
    this.authService.getProductAttributes().subscribe(
      (data: ProductAttribute[]) => {
        const groupedAttributesMap: Map<number, GroupedAttributes> = new Map();

        data.forEach(attr => {
          const productId = attr.product.id;
          if (productId !== undefined && productId !== null) {
            if (!groupedAttributesMap.has(productId)) {
              groupedAttributesMap.set(productId, {
                product: attr.product,
                attributes: [],
                selectedColorId: null, // Initialisation à null
                selectedSizeId: null,  // Initialisation à null
                selectedLengthId: null, // Initialisation à null
                availableColors: [],
                availableSizes: [],
                availableLengths: [],
                filteredAttributes: []
              });
            }
            const group = groupedAttributesMap.get(productId);
            if (group) {
              group.attributes.push({
                ...attr,
                imageUrl: attr.imagePath ? this.authService.getImageUrl(attr.imagePath) : null,
                textureUrl: attr.texturePath ? this.authService.getImageUrl(attr.texturePath) : null,
                videoUrl: attr.videoPath ? this.authService.getVideoUrl(attr.videoPath) : null
              });
            }
          }
        });

        this.productGroups = Array.from(groupedAttributesMap.values());

        // Initialiser les filtres et appliquer les filtres initiaux
        this.productGroups.forEach(group => {
          this.initializeFilterOptions(group);
          this.applyFilters(group);
        });
      },
      (error) => console.error('Erreur lors du chargement des attributs de produit', error)
    );
  }

  initializeFilterOptions(group: GroupedAttributes): void {
    const { attributes } = group;

    // Extraire toutes les couleurs uniques
    const colorMap = new Map<number, Color>();
    attributes.forEach(attr => {
      if (attr.color && !colorMap.has(attr.color.id)) {
        colorMap.set(attr.color.id, attr.color);
      }
    });
    group.availableColors = Array.from(colorMap.values());

    // Extraire toutes les tailles uniques
    const sizeMap = new Map<number, Size>();
    attributes.forEach(attr => {
      if (attr.size && !sizeMap.has(attr.size.id)) {
        sizeMap.set(attr.size.id, attr.size);
      }
    });
    group.availableSizes = Array.from(sizeMap.values());

    // Extraire toutes les longueurs uniques
    const lengthMap = new Map<number, Length>();
    attributes.forEach(attr => {
      if (attr.length && !lengthMap.has(attr.length.id)) {
        lengthMap.set(attr.length.id, attr.length);
      }
    });
    group.availableLengths = Array.from(lengthMap.values());

    // Initialiser les sélections par défaut à null (aucun filtre appliqué)
    group.selectedColorId = null;
    group.selectedSizeId = null;
    group.selectedLengthId = null;

    // Appliquer les filtres initiaux (aucun filtre)
    this.applyFilters(group);
  }

  setColor(group: GroupedAttributes, colorId: number | null): void {
    group.selectedColorId = colorId;
    // Réinitialiser les sélections dépendantes
    group.selectedSizeId = null;
    group.selectedLengthId = null;
    this.applyFilters(group);
  }

  setSize(group: GroupedAttributes, sizeId: number | null): void {
    group.selectedSizeId = sizeId;
    // Réinitialiser la sélection de longueur
    group.selectedLengthId = null;
    this.applyFilters(group);
  }

  setLength(group: GroupedAttributes, lengthId: number | null): void {
    group.selectedLengthId = lengthId;
    this.applyFilters(group);
  }

  applyFilters(group: GroupedAttributes): void {
    group.filteredAttributes = group.attributes.filter(attr => {
      const colorMatch = group.selectedColorId ? attr.color.id === group.selectedColorId : true;
      const sizeMatch = group.selectedSizeId ? attr.size.id === group.selectedSizeId : true;
      const lengthMatch = group.selectedLengthId ? attr.length?.id === group.selectedLengthId : true;
      return colorMatch && sizeMatch && lengthMatch;
    });
  }

  // Méthodes pour obtenir les options disponibles en fonction des filtres actuels
  getAvailableColors(group: GroupedAttributes): Color[] {
    const colors = new Map<number, Color>();
    group.attributes.forEach(attr => {
      // Vérifie si l'attribut actuel respecte uniquement les filtres de taille et longueur
      const sizeMatch = group.selectedSizeId ? attr.size.id === group.selectedSizeId : true;
      const lengthMatch = group.selectedLengthId ? attr.length?.id === group.selectedLengthId : true;

      if (sizeMatch && lengthMatch) {
        colors.set(attr.color.id, attr.color);
      }
    });
    return Array.from(colors.values());
  }

  getAvailableSizes(group: GroupedAttributes): Size[] {
    const sizes = new Map<number, Size>();
    group.attributes.forEach(attr => {
      // Vérifie si l'attribut actuel respecte uniquement les filtres de couleur et longueur
      const colorMatch = group.selectedColorId ? attr.color.id === group.selectedColorId : true;
      const lengthMatch = group.selectedLengthId ? attr.length?.id === group.selectedLengthId : true;

      if (colorMatch && lengthMatch) {
        sizes.set(attr.size.id, attr.size);
      }
    });
    return Array.from(sizes.values());
  }

  getAvailableLengths(group: GroupedAttributes): Length[] {
    const lengths = new Map<number, Length>();
    group.attributes.forEach(attr => {
      // Vérifie si l'attribut actuel respecte uniquement les filtres de couleur et taille
      const colorMatch = group.selectedColorId ? attr.color.id === group.selectedColorId : true;
      const sizeMatch = group.selectedSizeId ? attr.size.id === group.selectedSizeId : true;

      if (colorMatch && sizeMatch && attr.length) {
        lengths.set(attr.length.id, attr.length);
      }
    });
    return Array.from(lengths.values());
  }

  // Gestion des événements de changement dans les sélecteurs
  onColorChange(event: Event, group: GroupedAttributes): void {
    const selectElement = event.target as HTMLSelectElement;
    const colorId = selectElement.value ? parseInt(selectElement.value, 10) : null;
    this.setColor(group, colorId);
  }

  onSizeChange(event: Event, group: GroupedAttributes): void {
    const selectElement = event.target as HTMLSelectElement;
    const sizeId = selectElement.value ? parseInt(selectElement.value, 10) : null;
    this.setSize(group, sizeId);
  }

  onLengthChange(event: Event, group: GroupedAttributes): void {
    const selectElement = event.target as HTMLSelectElement;
    const lengthId = selectElement.value ? parseInt(selectElement.value, 10) : null;
    this.setLength(group, lengthId);
  }

  addToCart(attribute: ProductAttribute, quantity: number): void {
    const cartItem: CartOrderItem = {
      item: attribute.product.title,
      imagePath: attribute.imagePath,
      attribute: attribute.id?.toString() || '',
      quantity: quantity, // Utilisation de la quantité fournie
      price: attribute.price,
      totalAmt: attribute.price * quantity,
    };
    const status = attribute.product.status;
    const isFeatured = attribute.product.isFeatured;
    this.cartService.addToCart(cartItem);
  }

}
